/**
 * People by program (1.5.9): the program tabs filter People. One place for the rules the page, the Download, the
 * tenant search and Admin → Reports share: the five choices of the tab row (the three programs, Everyone, No
 * program yet), a person's status in one program, the chips and their counts, the sort, and the CSV.
 *
 * Nothing here reads or writes a list: the server builds each person's line per program (api/src/people) and these
 * functions only sort, count and word what it sent (docs/DECISIONS.md D-1.5.9-n).
 */
import { isStaffCategory, isTenantActive, type ProgramKey } from './people';

/* ------------------------------------------------------------------------------------------------
 * The choices in the tab row
 * ---------------------------------------------------------------------------------------------- */

/** The program strip's order (Housekeeping · Companion · Reading & Services), then the two choices People adds. */
export const STRIP_PROGRAMS = ['housekeeping', 'companion', 'reading'] as const satisfies readonly ProgramKey[];
export const PEOPLE_VIEWS = ['housekeeping', 'companion', 'reading', 'everyone', 'none'] as const;
export type PeopleView = (typeof PEOPLE_VIEWS)[number];
/**
 * 25 Oct 2026 (D-1025-ADMIN): People's own row of views also has Tech Support. It is a view of the People page only —
 * the program strip and its remembered choice (src/app/program.tsx) keep the five above.
 */
export const PEOPLE_PAGE_VIEWS = ['housekeeping', 'companion', 'reading', 'tech', 'everyone', 'none'] as const;
export type PeoplePageView = (typeof PEOPLE_PAGE_VIEWS)[number];

/** What the address carries: `?program=HK`. */
export const VIEW_CODES: Record<PeoplePageView, string> = { housekeeping: 'HK', companion: 'COMP', reading: 'READ', tech: 'TECH', everyone: 'ALL', none: 'NONE' };

export function viewOfCode(code: string | null | undefined): PeoplePageView | null {
  const c = (code ?? '').trim().toUpperCase();
  return PEOPLE_PAGE_VIEWS.find((v) => VIEW_CODES[v] === c) ?? null;
}

/** One of the three programs with their own statuses (Tech Support is a view of People, not a program line). */
export const isProgramView = (v: PeoplePageView): v is ProgramKey => v !== 'everyone' && v !== 'none' && v !== 'tech';

/** The programs' names in English, for the CSV (the screen's words come from i18n). */
export const PROGRAM_NAME_EN: Record<ProgramKey, string> = { housekeeping: 'Housekeeping', companion: 'Companion', reading: 'Reading & Services' };

/* ------------------------------------------------------------------------------------------------
 * One person in one program
 * ---------------------------------------------------------------------------------------------- */

export const PROGRAM_STATUSES = ['canBook', 'signingUp', 'paused', 'standby', 'inactive'] as const;
export type ProgramStatus = (typeof PROGRAM_STATUSES)[number];
/** Enrolled: an active or a paused enrollment. The standby list and the inactive are beside the program, not in it. */
export const ENROLLED_STATUSES: readonly ProgramStatus[] = ['canBook', 'signingUp', 'paused'];
export const isEnrolled = (s: ProgramStatus | undefined): boolean => !!s && ENROLLED_STATUSES.includes(s);

export type MissingKey = 'release' | 'homeVisit';

/** The import's note on a tenant who is on Housekeeping's standby list and in no program (D-1.5.2-9). */
export const HK_STANDBY_NOTE = 'On the housekeeping standby list';

export interface ProgramLine {
  status: ProgramStatus;
  /** Signing up: what Housekeeping still waits for. */
  missing: MissingKey[];
  /** Paused: by a safety hold, or by a person's pause (with the reason they wrote, when it is on the row). */
  pausedBy: 'hold' | 'pause' | null;
  pausedReason: string | null;
  /** Housekeeping's two gates; null for a program that has neither. */
  release: boolean | null;
  homeVisit: boolean | null;
  /** Visits this cycle: used of authorized (null = no fixed limit). Housekeeping only. */
  cycle: { used: number; authorized: number | null } | null;
  /** The standing pattern, as stored (Second · Tuesday · Morning), the primary first. */
  pattern: { week: string; day: string; period: string }[];
  /** The next proposed or booked day from today on. */
  next: { dayKey: string; state: 'proposed' | 'booked' } | null;
  /**
   * Housekeeping: the tenant's record carries the standby note. Someone enrolled can be on the standby list too (the
   * first live import: ten people with an active enrollment and the note) — they keep their status and are also
   * counted on the standby chip.
   */
  standbyList: boolean;
}

/** On the program's standby list: waiting for a place, or enrolled and noted as standby. */
export const onStandbyList = (l: ProgramLine | undefined): boolean => !!l && (l.status === 'standby' || l.standbyList);

export interface ProgramLineFacts {
  program: ProgramKey;
  tenantStatus: string;
  enrollmentStatus: string | null;
  /** Housekeeping: all five conditions met (the list's own Eligible). */
  eligible: boolean;
  waiver: boolean;
  homeVisit: boolean;
  hold: boolean;
  standbyNote: boolean;
}

/** The person's status in the program; null when they have nothing to do with it. */
export function programStatusOf(f: ProgramLineFacts): Pick<ProgramLine, 'status' | 'missing' | 'pausedBy'> | null {
  const as = (status: ProgramStatus, missing: MissingKey[] = [], pausedBy: ProgramLine['pausedBy'] = null) => ({ status, missing, pausedBy });
  const noted = f.program === 'housekeeping' && f.standbyNote;
  const enr = f.enrollmentStatus || null;
  if (!enr) return noted ? as(f.tenantStatus === 'Active' ? 'standby' : 'inactive') : null;
  if (f.tenantStatus !== 'Active') return as('inactive');
  if (enr === 'Ended' || enr === 'Not Approved') return as(noted ? 'standby' : 'inactive');
  if (enr === 'Paused') return as('paused', [], 'pause');
  if (enr === 'Waitlisted') return as('standby');
  if (f.program !== 'housekeeping') return as(enr === 'Active' ? 'canBook' : 'signingUp');
  if (f.hold) return as('paused', [], 'hold');
  if (f.eligible) return as('canBook');
  return as('signingUp', [...(f.waiver ? [] : (['release'] as const)), ...(f.homeVisit ? [] : (['homeVisit'] as const))]);
}

/* ------------------------------------------------------------------------------------------------
 * The list: which people a choice shows, the chips, the counts
 * ---------------------------------------------------------------------------------------------- */

/** What these rules need of a person (shared/people-api.ts PersonRow has it all). */
export interface ProgramPerson {
  name: string;
  firstName: string;
  lastName: string;
  unit: string;
  phone: string | null;
  email: string | null;
  status: string;
  category: string;
  lines: Partial<Record<ProgramKey, ProgramLine>>;
  /** 25 Oct 2026: Tech Support has an enrollment or a visit on record (it has no line of its own). */
  tech?: boolean;
}

const enrolledAnywhere = (p: ProgramPerson): boolean => STRIP_PROGRAMS.some((k) => isEnrolled(p.lines[k]?.status)) || !!p.tech;

/**
 * The people a choice is about. A program: everyone with a line in it (the chips then say which). Everyone: the
 * whole list. No program yet: active tenants enrolled in nothing — never a staff unit, never someone who moved out.
 */
export function viewRows<P extends ProgramPerson>(people: readonly P[], view: PeoplePageView): P[] {
  if (view === 'everyone') return [...people];
  if (view === 'tech') return people.filter((p) => !!p.tech && !isStaffCategory(p.category));
  if (view === 'none') return people.filter((p) => isTenantActive(p.status) && !isStaffCategory(p.category) && !enrolledAnywhere(p));
  return people.filter((p) => !!p.lines[view]);
}

export function chipCounts(inView: readonly ProgramPerson[], program: ProgramKey): Record<ProgramStatus, number> {
  const out = Object.fromEntries(PROGRAM_STATUSES.map((s) => [s, 0])) as Record<ProgramStatus, number>;
  for (const p of inView) {
    const l = p.lines[program];
    if (!l) continue;
    out[l.status]++;
    if (l.standbyList && l.status !== 'standby') out.standby++;
  }
  return out;
}

/**
 * With no chip on, the program's enrollees; with chips on, exactly the groups pressed (several can be on). The
 * standby chip also shows the enrolled people noted as standby.
 */
export function applyChips<P extends ProgramPerson>(inView: readonly P[], program: ProgramKey, chips: readonly ProgramStatus[]): P[] {
  const want = chips.length ? chips : ENROLLED_STATUSES;
  return inView.filter((p) => {
    const l = p.lines[program];
    return !!l && (want.includes(l.status) || (chips.includes('standby') && l.standbyList));
  });
}

export function enrolledCount(people: readonly ProgramPerson[], program: ProgramKey): number {
  return people.filter((p) => isEnrolled(p.lines[program]?.status)).length;
}

/** No program yet has one chip: the people on a standby list. */
export const onStandby = (p: ProgramPerson): boolean => STRIP_PROGRAMS.some((k) => onStandbyList(p.lines[k]));
export function noneChipCounts(inView: readonly ProgramPerson[]): { standby: number } {
  return { standby: inView.filter(onStandby).length };
}

export type ProgramCounts = Record<'enrolled' | ProgramStatus, number>;

/** Admin → Reports → People by program: the count table. */
export function programCounts(people: readonly ProgramPerson[]): Record<ProgramKey, ProgramCounts> {
  const of = (k: ProgramKey): ProgramCounts => ({ enrolled: enrolledCount(people, k), ...chipCounts(viewRows(people, k), k) });
  return { housekeeping: of('housekeeping'), reading: of('reading'), companion: of('companion') };
}

/* ------------------------------------------------------------------------------------------------
 * Sort
 * ---------------------------------------------------------------------------------------------- */

export const PEOPLE_SORTS = ['apartment', 'name', 'status', 'nextVisit'] as const;
export type PeopleSort = (typeof PEOPLE_SORTS)[number];

/** Apartments as a person reads them: 2B before 10A. */
export function compareUnits(a: string, b: string): number {
  return a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' });
}

const byName = (a: ProgramPerson, b: ProgramPerson) => a.lastName.localeCompare(b.lastName, 'en', { sensitivity: 'base' }) || a.firstName.localeCompare(b.firstName, 'en', { sensitivity: 'base' });
const byUnit = (a: ProgramPerson, b: ProgramPerson) => compareUnits(a.unit, b.unit) || byName(a, b);

/** The soonest next day a person has: in the program chosen, or in any program when none is. */
export function nextDayOf(p: ProgramPerson, program: ProgramKey | null): string | null {
  const days = (program ? [p.lines[program]] : STRIP_PROGRAMS.map((k) => p.lines[k])).map((l) => l?.next?.dayKey).filter((d): d is string => !!d);
  return days.length ? days.sort()[0]! : null;
}

function statusRank(p: ProgramPerson, program: ProgramKey | null): number {
  const ranks = (program ? [p.lines[program]] : STRIP_PROGRAMS.map((k) => p.lines[k])).map((l) => (l ? PROGRAM_STATUSES.indexOf(l.status) : PROGRAM_STATUSES.length));
  return Math.min(PROGRAM_STATUSES.length, ...ranks);
}

/** A new list in the order asked (apartment breaks every tie). */
export function sortPeople<P extends ProgramPerson>(rows: readonly P[], sort: PeopleSort, program: ProgramKey | null): P[] {
  const out = [...rows];
  switch (sort) {
    case 'apartment':
      return out.sort(byUnit);
    case 'name':
      return out.sort((a, b) => byName(a, b) || compareUnits(a.unit, b.unit));
    case 'status':
      return out.sort((a, b) => statusRank(a, program) - statusRank(b, program) || byUnit(a, b));
    case 'nextVisit':
      return out.sort((a, b) => (nextDayOf(a, program) ?? '99999999').localeCompare(nextDayOf(b, program) ?? '99999999') || byUnit(a, b));
  }
}

/* ------------------------------------------------------------------------------------------------
 * Words (English — the CSV; the screen says the same through i18n) and the CSV
 * ---------------------------------------------------------------------------------------------- */

const STATUS_EN: Record<ProgramStatus, string> = { canBook: 'Can be booked', signingUp: 'Signed up, not active yet', paused: 'Paused', standby: 'On the standby list', inactive: 'Inactive' };
const MISSING_EN: Record<MissingKey, string> = { release: 'release', homeVisit: 'home visit' };

export function statusText(l: ProgramLine): string {
  const base = baseStatusText(l);
  return l.standbyList && l.status !== 'standby' ? `${base}; on the standby list` : base;
}

function baseStatusText(l: ProgramLine): string {
  // 1.6.8 (D-1.6.8-4): nobody is "signing up" — a person is signed up, and what is missing is named
  if (l.status === 'signingUp' && l.missing.length) return `Signed up (missing: ${l.missing.map((m) => MISSING_EN[m]).join(' and ')})`;
  if (l.status === 'paused') {
    const why = l.pausedBy === 'hold' ? 'safety hold' : (l.pausedReason ?? '');
    return why ? `${STATUS_EN.paused} (${why})` : STATUS_EN.paused;
  }
  return STATUS_EN[l.status];
}

export function patternText(l: ProgramLine | undefined): string {
  return (l?.pattern ?? []).map((p) => [p.week, p.day, p.period].filter(Boolean).join(' ')).join('; ');
}

export const CSV_HEADER = ['Full name', 'Apartment', 'Phone', 'Email', 'Program status', 'Release', 'Home visit', 'Pattern'] as const;

/** A cell that can never start a formula in a spreadsheet, quoted when it holds a comma, a quote or a line break. */
export function csvCell(v: string): string {
  const safe = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

const yesNo = (v: boolean | null | undefined): string => (v === true ? 'Yes' : v === false ? 'No' : '');

/**
 * Download this list: one line per person in the order given. A program's list says that program's status;
 * Everyone and No program yet say every program the person has a line in, and Housekeeping's release, home visit
 * and pattern.
 */
export function peopleCsv(rows: readonly ProgramPerson[], view: PeoplePageView): string {
  const body = rows.map((p) => {
    const l = isProgramView(view) ? p.lines[view] : p.lines.housekeeping;
    const status = isProgramView(view)
      ? l
        ? statusText(l)
        : ''
      : STRIP_PROGRAMS.filter((k) => p.lines[k])
          .map((k) => `${PROGRAM_NAME_EN[k]}: ${statusText(p.lines[k]!)}`)
          .concat(p.tech ? ['Tech Support'] : [])
          .join('; ') || 'No program yet';
    return [p.name, p.unit, p.phone ?? '', p.email ?? '', status, yesNo(l?.release), yesNo(l?.homeVisit), patternText(l)].map(csvCell).join(',');
  });
  return [CSV_HEADER.join(','), ...body].join('\r\n') + '\r\n';
}

export function csvFileName(view: PeoplePageView, isoDay: string): string {
  return `people-${view === 'everyone' ? 'everyone' : view === 'none' ? 'no-program' : view}-${isoDay}.csv`;
}

/* ------------------------------------------------------------------------------------------------
 * The person's card: its actions, in order (1.5.12)
 * ---------------------------------------------------------------------------------------------- */

/** An enrollment that counts as being in the program — the same as the list's Enrolled (active, signing up, paused). */
const ENROLLED_ENROLLMENT = (s: string | null | undefined): boolean => !!s && s !== 'Ended' && s !== 'Not Approved' && s !== 'Waitlisted';

/**
 * The card's own "No program yet": an active tenant (never a staff unit, never someone who moved out) enrolled in
 * nothing — the people on the No program yet list, by the same rule (`viewRows(…, 'none')`).
 */
export function inNoProgram(p: { status: string; category: string; programs: readonly { enrollmentStatus: string | null }[] }): boolean {
  return isTenantActive(p.status) && !isStaffCategory(p.category) && !p.programs.some((v) => ENROLLED_ENROLLMENT(v.enrollmentStatus));
}

export const PROFILE_ACTIONS = ['signUpProgram', 'editContact', 'editCompanion', 'changeStatus', 'call', 'history'] as const;
export type ProfileAction = (typeof PROFILE_ACTIONS)[number];

export interface ProfileActionFacts {
  /** `inNoProgram` of the person. */
  noProgram: boolean;
  /** The signed-in person may sign people up (administrator, co-administrator). */
  canSignUp: boolean;
  /** The person has Companion details to edit. */
  companion: boolean;
  phone: boolean;
}

/**
 * The actions under the person's card, in the order they are shown. Someone in no program (imported from a roster,
 * on the No program yet list) is offered Sign up for a program first; for everyone else nothing changes.
 */
export function profileActions(f: ProfileActionFacts): ProfileAction[] {
  return [
    ...(f.noProgram && f.canSignUp ? (['signUpProgram'] as const) : []),
    'editContact',
    ...(f.companion ? (['editCompanion'] as const) : []),
    'changeStatus',
    ...(f.phone ? (['call'] as const) : []),
    'history',
  ];
}
